<template>
  <teleport to="body">
    <!-- ═══ MAIN MENU ═══ -->
    <div
      v-if="isMobileOrTablet"
      class="dnm"
      :class="[
        `dnm--${isDragging ? dragCurrentSide : side}`,
        { 'dnm--open': isOpen, 'dnm--dragging': isDragging },
      ]"
      :style="menuStyle"
    >
      <!-- Toggle arrow (always visible) -->
      <button class="dnm__toggle" :aria-label="isOpen ? 'Chiudi' : 'Apri'" @click="!isDragging && toggle()">
        <v-icon size="16">{{ toggleIcon }}</v-icon>
      </button>

      <!-- Panel (slides in/out via width transition) -->
      <div class="dnm__panel">
        <!-- Drag handle -->
        <div
          class="dnm__drag"
          @touchstart.prevent="onDragStart"
          @touchmove.prevent="onDragMove"
          @touchend.prevent="onDragEnd"
        >
          <v-icon size="18" class="text-medium-emphasis">ms:drag-indicator</v-icon>
        </div>

        <div class="dnm__divider" />

        <!-- Navigation icons -->
        <button
          v-for="item in items"
          :key="item.id"
          class="dnm__item"
          :aria-label="item.label"
          :title="item.label"
          @click="!isDragging && scrollToSection(item.id)"
        >
          <v-icon size="20">ms:{{ item.icon }}</v-icon>
        </button>
      </div>
    </div>

    <!-- ═══ GHOST always shows where menu will land ═══ -->
    <div
      v-if="isMobileOrTablet && isDragging"
      class="dnm-ghost"
      :class="`dnm-ghost--${dragCurrentSide}`"
      :style="{ top: '25vh' }"
    />
  </teleport>
</template>

<script setup lang="ts">
/**
 * GgDetailNavMenu — Floating section navigation for detail pages (mobile only)
 *
 * @version 1.1.0
 * @description Draggable floating menu. During drag the menu follows the finger
 *              and a ghost placeholder stays at the original position.
 */

import { computed, ref, type CSSProperties } from 'vue'
import { useResponsive } from '@/composables/useResponsive'
import { useDetailNavMenu, type DetailNavItem } from '@/composables/useDetailNavMenu'

defineOptions({ name: 'GgDetailNavMenu' })

defineProps<{
  items: DetailNavItem[]
}>()

const { isMobileOrTablet } = useResponsive()
const {
  isOpen,
  side,
  isDragging,
  toggle,
  setSide,
  scrollToSection,
} = useDetailNavMenu()

// ── Drag state ───────────────────────────────────────────────────────────────

const dragX = ref(0)
const dragY = ref(0)
const dragCurrentSide = ref<'left' | 'right'>('left')
let dragStartX = 0
let dragStartY = 0
let menuStartRect: DOMRect | null = null

const toggleIcon = computed(() => {
  const s = isDragging.value ? dragCurrentSide.value : side.value
  if (s === 'left') return isOpen.value ? 'ms:chevron-left' : 'ms:chevron-right'
  return isOpen.value ? 'ms:chevron-right' : 'ms:chevron-left'
})

/** While dragging: position via translate. At rest: anchored to edge at 25vh */
const menuStyle = computed<CSSProperties>(() => {
  if (isDragging.value) {
    return {
      position: 'fixed',
      left: `${dragX.value}px`,
      top: `${dragY.value}px`,
      right: 'auto',
      transition: 'none',
      zIndex: 1031,
    } as CSSProperties
  }
  return { top: '25vh' } as CSSProperties
})

// ── Drag handlers ────────────────────────────────────────────────────────────

function onDragStart(e: TouchEvent) {
  const touch = e.touches[0]
  dragStartX = touch.clientX
  dragStartY = touch.clientY

  // Get current menu position
  const menuEl = (e.target as HTMLElement).closest('.dnm') as HTMLElement
  if (menuEl) {
    menuStartRect = menuEl.getBoundingClientRect()
    dragX.value = menuStartRect.left
    dragY.value = menuStartRect.top
  }

  dragCurrentSide.value = side.value
  isDragging.value = true
}

function onDragMove(e: TouchEvent) {
  if (!menuStartRect) return
  const touch = e.touches[0]
  const dx = touch.clientX - dragStartX
  const dy = touch.clientY - dragStartY

  dragX.value = menuStartRect.left + dx
  dragY.value = menuStartRect.top + dy

  // Determine which side the finger is on
  const mid = window.innerWidth / 2
  dragCurrentSide.value = touch.clientX < mid ? 'left' : 'right'
}

function onDragEnd(e: TouchEvent) {
  const touch = e.changedTouches[0]
  const mid = window.innerWidth / 2
  const newSide = touch.clientX < mid ? 'left' : 'right'

  if (newSide !== side.value) {
    setSide(newSide)
  }

  isDragging.value = false
  dragCurrentSide.value = side.value
  menuStartRect = null
}
</script>

<style lang="scss" scoped>
@use '@/themes/variables' as *;

$toggle-w: 24px;
$toggle-h: 44px;
$panel-w: 44px;
$item-size: 38px;
$radius: 10px;
$bg: rgba(var(--v-theme-surface), 0.92);
$border-clr: rgba(var(--v-theme-on-surface), 0.10);
$shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
$speed: 0.2s cubic-bezier(0.4, 0, 0.2, 1);

// =============================================================================
// Container — fixed, sticks to edge
// =============================================================================

.dnm {
  position: fixed;
  z-index: $z-index-fixed;
  display: flex;
  align-items: flex-start;
  pointer-events: auto;
  transition: left $speed, right $speed, top $speed;

  &--dragging {
    transition: none;
    opacity: 0.9;
  }
}

// =============================================================================
// LEFT side
// =============================================================================

.dnm--left {
  left: 0;
  flex-direction: row;

  .dnm__toggle {
    order: 2;
    border-radius: 0 $radius $radius 0;
    border-left: none;
  }

  .dnm__panel {
    order: 1;
    border-radius: 0 $radius $radius 0;
    border-left: none;
    max-width: 0;
    padding: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-width $speed, padding $speed, opacity $speed;
  }

  &.dnm--open .dnm__panel {
    max-width: $panel-w + 16px;
    padding: $spacing-xs;
    opacity: 1;
  }
}

// =============================================================================
// RIGHT side
// =============================================================================

.dnm--right {
  right: 0;
  flex-direction: row-reverse;

  .dnm__toggle {
    order: 2;
    border-radius: $radius 0 0 $radius;
    border-right: none;
  }

  .dnm__panel {
    order: 1;
    border-radius: $radius 0 0 $radius;
    border-right: none;
    max-width: 0;
    padding: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-width $speed, padding $speed, opacity $speed;
  }

  &.dnm--open .dnm__panel {
    max-width: $panel-w + 16px;
    padding: $spacing-xs;
    opacity: 1;
  }
}

// When dragging, override left/right anchoring — position is set inline
.dnm--dragging {
  &.dnm--left { left: auto; }
  &.dnm--right { right: auto; }
}

// =============================================================================
// Toggle button
// =============================================================================

.dnm__toggle {
  width: $toggle-w;
  height: $toggle-h;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $bg;
  border: 1px solid $border-clr;
  box-shadow: $shadow;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  backdrop-filter: blur(8px);

  .v-icon { color: rgba(var(--v-theme-on-surface), 0.45); }
}

// =============================================================================
// Panel
// =============================================================================

.dnm__panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: $bg;
  border: 1px solid $border-clr;
  box-shadow: $shadow;
  backdrop-filter: blur(8px);
}

// =============================================================================
// Drag handle
// =============================================================================

.dnm__drag {
  width: $item-size;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;
  &:active { cursor: grabbing; }
}

.dnm__divider {
  width: 22px;
  height: 1px;
  background: $border-clr;
  margin: 1px 0;
}

// =============================================================================
// Nav item
// =============================================================================

.dnm__item {
  width: $item-size;
  height: $item-size;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $border-radius;
  background: transparent;
  border: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease;

  .v-icon { color: rgba(var(--v-theme-on-surface), 0.65); }

  &:active {
    background: rgba(var(--v-theme-primary), 0.12);
    .v-icon { color: rgb(var(--v-theme-primary)); }
  }
}

// =============================================================================
// Ghost — stays at original position during drag
// =============================================================================

.dnm-ghost {
  position: fixed;
  z-index: $z-index-fixed - 1;
  width: 40px;
  height: 72px;
  background: rgba(var(--v-theme-primary), 0.06);
  border: 2px dashed rgba(var(--v-theme-primary), 0.25);
  pointer-events: none;
  animation: dnm-pulse 0.8s ease infinite;

  &--left {
    left: 0;
    border-left: none;
    border-radius: 0 $radius $radius 0;
  }
  &--right {
    right: 0;
    border-right: none;
    border-radius: $radius 0 0 $radius;
  }
}

@keyframes dnm-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
</style>
